import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-eb1cb1f6"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "map" }
const _hoisted_2 = { class: "map__popups" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { id: "map" }, null, -1 /* HOISTED */))

import { onMounted, Ref, ref, shallowRef } from 'vue'
import axios, { AxiosError } from 'axios'

import MapProfiles from '@/mapSearch/components/MapProfiles/MapProfiles.vue'
import MapFilters from '@/mapSearch/components/MapFilters/MapFilters.vue'

import api from '@/mapSearch/api'
import replaceEmptyStringsWithZeros from '@/utils/replaceEmptyStringsWithZeros'
import type { IMapFilters } from '@/mapSearch/types/MapFilters'
import type ProfileCardInterface from '@/common/types/ProfileCardInterface'


export default /*#__PURE__*/_defineComponent({
  __name: 'MapSearch',
  setup(__props) {

const map: Ref<any | null> = shallowRef(null)
const clusterer: Ref<any | null> = shallowRef(null)
const placemarksById: Ref<Record<number, any>> = shallowRef({})
const clustererAddedToMap = shallowRef(false)
const ymapsApi = shallowRef<any>(null)

const mapIsLoaded = ref(false)
const selectedPointId: Ref<number | null> = ref(null)

const profileCards: Ref<ProfileCardInterface[]> = ref([])
const isFiltersVisible = ref(false)
const isProfilesListVisible = ref(false)
const currentProfileIds: Ref<number[]> = ref([])

function toggleFilters() {
  isFiltersVisible.value = !isFiltersVisible.value
  isProfilesListVisible.value = false
}

function toggleList() {
  isProfilesListVisible.value = !isProfilesListVisible.value
  isFiltersVisible.value = false
}

async function showProfiles(filters: IMapFilters) {
  const cleanFilters = replaceEmptyStringsWithZeros(JSON.parse(JSON.stringify(filters)))

  try {
    const res = await axios.post('/api/map/profiles', { ...cleanFilters })
    setProfilesFromGeoJson(res.data)
  } catch (e: unknown) {
    if (e instanceof AxiosError) {
      if (e.response?.status === 400) {
        window.showNotification(e.response.data?.error?.message, 'error')
      } else {
        console.log(e)
        window.showNotification('Произошла ошибка на сервере!', 'error')
      }
    } else {
      console.log(e)
      window.showNotification('Произошла ошибка на сервере!', 'error')
    }
  }
}

const typeColors = ['#007AFF', '#00C7BE', '#F03535']

function calcClusterPieRadius(count: number) {
  // "ступенчатое" увеличение: выглядит предсказуемо и не раздувает кластеры слишком сильно
  if (count < 10) return 22
  if (count < 100) return 26
  if (count < 1000) return 28
  return 36
}

function applyDynamicClusterSizes() {
  const cl = clusterer.value
  if (!cl || typeof cl.getClusters !== 'function') return

  const clusters = cl.getClusters() as any[]
  for (const c of clusters) {
    const count = typeof c?.getGeoObjects === 'function' ? (c.getGeoObjects() as any[])?.length ?? 0 : 0
    const r = calcClusterPieRadius(count)
    const core = Math.max(10, Math.round(r * 0.45))
    c?.options?.set?.({
      clusterIconPieChartRadius: r,
      clusterIconPieChartCoreRadius: core
    })
  }
}

let clusterResizeRaf = 0
function scheduleDynamicClusterResize() {
  if (typeof window === 'undefined') return
  if (clusterResizeRaf) window.cancelAnimationFrame(clusterResizeRaf)
  clusterResizeRaf = window.requestAnimationFrame(() => {
    clusterResizeRaf = 0
    applyDynamicClusterSizes()
  })
}

function waitForYmaps2(): Promise<any> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('ymaps доступен только в браузере'))
  }

  if ((window as any).ymaps) return Promise.resolve((window as any).ymaps)

  return new Promise((resolve, reject) => {
    const startedAt = Date.now()
    const timeoutMs = 10_000

    const timer = window.setInterval(() => {
      if ((window as any).ymaps) {
        window.clearInterval(timer)
        resolve((window as any).ymaps)
        return
      }

      if (Date.now() - startedAt > timeoutMs) {
        window.clearInterval(timer)
        reject(new Error('Не удалось загрузить Yandex Maps API v2.1 (window.ymaps отсутствует)'))
      }
    }, 50)
  })
}

function resetSelection() {
  selectedPointId.value = null
  updatePlacemarkColors()
}

function updatePlacemarkColors() {
  const items = placemarksById.value
  for (const key of Object.keys(items)) {
    const id = Number(key)
    const pm = items[id]
    const type = pm?.properties?.get?.('type')
    const typeIdx = typeof type === 'number' ? type - 1 : -1
    const baseColor = typeIdx >= 0 && typeIdx < typeColors.length ? typeColors[typeIdx] : '#ccc'
    const isSelected = selectedPointId.value === id
    pm?.options?.set?.('iconColor', isSelected ? 'rgba(251, 96, 30, 1)' : baseColor)
  }
}

async function openProfilesByIds(profileIds: number[]) {
  if (JSON.stringify(profileIds) === JSON.stringify(currentProfileIds.value)) {
    isProfilesListVisible.value = true
    return
  }

  currentProfileIds.value = profileIds
  profileCards.value = await api.getProfileByIds(profileIds)
  isProfilesListVisible.value = true
  isFiltersVisible.value = false

  if (window.innerWidth < 768) {
    setTimeout(() => {
      document.querySelector('.profiles-list')?.scrollIntoView({ behavior: 'smooth', block: 'end' })
      window.scrollBy(0, 70);
    })
  }
}

function createClusterer(ymaps: any) {
  const clusterer = new ymaps.Clusterer({
    clusterIconLayout: 'default#pieChart',
    clusterIconPieChartRadius: 25,
    clusterIconPieChartCoreRadius: 10,
    clusterIconPieChartStrokeWidth: 3,
    hasBalloon: false
  })

  // Клик по кластеру - открываем список профилей в кластере + приближаем дефолт Яндекс карты)
  clusterer.events.add('click', async (e: any) => {
    e.preventDefault()
    const target = e.get('target')

    // Клик по кластеру
    if (target && typeof target.getGeoObjects === 'function') {
      resetSelection()

      const geoObjects = target.getGeoObjects() as any[]
      const ids = geoObjects
        .map((o) => o?.properties?.get?.('idProfile'))
        .filter((id: unknown): id is number => typeof id === 'number')
        .slice(0, 99)

      await openProfilesByIds(ids)
      return
    }
  })

  return clusterer
}

function setProfilesFromGeoJson(geoJson: any) {
  if (!map.value || !ymapsApi.value) return

  // Сбрасываем выделение (логика совпадает с прежней: выбор точки/кластера сбрасывается при обновлении данных)
  resetSelection()

  if (clustererAddedToMap.value && clusterer.value) {
    try {
      map.value.geoObjects.remove(clusterer.value)
    } catch {
      // ignore
    }
  }
  clusterer.value = createClusterer(ymapsApi.value)
  clustererAddedToMap.value = false
  placemarksById.value = {}

  const features = geoJson?.features
  if (!Array.isArray(features)) return

  const placemarks: any[] = []

  for (const f of features) {
    const geometry = f?.geometry
    const props = f?.properties
    const coords = geometry?.coordinates

    if (geometry?.type !== 'Point' || !Array.isArray(coords) || coords.length < 2) continue

    // backend отдаёт GeoJSON в порядке [lng, lat]
    const lng = Number.parseFloat(coords[0])
    const lat = Number.parseFloat(coords[1])
    const idProfile = props?.idProfile
    const type = props?.type

    if (!Number.isFinite(lat) || !Number.isFinite(lng) || typeof idProfile !== 'number') continue
    // защита от "битых" координат — они могут ронять внутреннюю математику API 2.1
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) continue

    const typeIdx = typeof type === 'number' ? type - 1 : -1
    const color = typeIdx >= 0 && typeIdx < typeColors.length ? typeColors[typeIdx] : '#ccc'

    const placemark = new (window as any).ymaps.Placemark(
      [lat, lng],
      { idProfile, type },
      {
        preset: 'islands#icon',
        iconColor: color
      }
    )

    placemark.events.add('click', async () => {
      resetSelection()
      selectedPointId.value = idProfile
      updatePlacemarkColors()
      await openProfilesByIds([idProfile])
    })

    placemarksById.value[idProfile] = placemark
    placemarks.push(placemark)
  }

  // Важно: не вызываем add([]) — в 2.1 встречается падение внутри API.
  if (placemarks.length === 0) return

  clusterer.value.add(placemarks)
  map.value.geoObjects.add(clusterer.value)
  clustererAddedToMap.value = true

  // Кластеры формируются/пересчитываются внутри API — после добавления попросим обновить размеры.
  scheduleDynamicClusterResize()
}

function handleMapProfilesClick(e: MouseEvent) {
  if (window.innerWidth >= 768) return

  const target = e.target as Node
  const isProfileCard = target instanceof HTMLElement && target.closest('.profile-card')

  if (!isProfileCard) {
    isProfilesListVisible.value = false
    setTimeout(() => {
      document.querySelector('#map')?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    })
  }
}

onMounted(async () => {
  const ymaps = await waitForYmaps2()

  await new Promise<void>((resolve) => ymaps.ready(resolve))
  ymapsApi.value = ymaps

  map.value = new ymaps.Map(
    'map',
    {
      center: [55.7522, 37.6156], // [lat, lng]
      zoom: 9,
      controls: ['zoomControl']
    },
    {
      suppressMapOpenBlock: true
    }
  )

  clusterer.value = createClusterer(ymaps)
  clustererAddedToMap.value = false
  mapIsLoaded.value = true

  // Клик по пустой карте — закрываем список профилей
  map.value.events.add('click', (e: any) => {
    const target = e.get('target')
    if (target !== map.value) return

    isProfilesListVisible.value = false
    if (window.innerWidth < 768) {
      setTimeout(() => {
        document.querySelector('#map')?.scrollIntoView({ behavior: 'smooth', block: 'end' })
      })
    }
  })

  // При изменении масштаба/границ кластеры пересобираются — пересчитаем размеры.
  map.value.events.add('boundschange', (e) => {
    const oldZoom = e.get('oldZoom')
    const newZoom = e.get('newZoom')

    if (newZoom === oldZoom) return

    if (!clustererAddedToMap.value) return
    scheduleDynamicClusterResize()
  })
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (mapIsLoaded.value)
        ? (_openBlock(), _createBlock(MapFilters, {
            key: 0,
            visible: isFiltersVisible.value,
            class: "map__popup",
            onToggleFilters: _cache[0] || (_cache[0] = () => toggleFilters()),
            onShowProfiles: showProfiles
          }, null, 8 /* PROPS */, ["visible"]))
        : _createCommentVNode("v-if", true),
      _createVNode(MapProfiles, {
        visible: isProfilesListVisible.value,
        cards: profileCards.value,
        class: "map__popup",
        onToggleList: _cache[1] || (_cache[1] = () => toggleList()),
        onClick: handleMapProfilesClick
      }, null, 8 /* PROPS */, ["visible", "cards"])
    ]),
    _hoisted_3
  ]))
}
}

})